if (!customElements.get('valesstra-v2-gallery')) {
  customElements.define(
    'valesstra-v2-gallery',
    class ValesstraV2Gallery extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-v2-gallery-track]');
        this.items = Array.from(this.querySelectorAll('[data-v2-gallery-item]'));
        this.progress = this.querySelector('[data-v2-gallery-progress]');
        this.current = this.querySelector('[data-v2-gallery-current]');
        this.status = this.querySelector('[data-v2-gallery-status]');
        this.targets = Array.from(this.querySelectorAll('[data-v2-gallery-target]'));
        this.previous = this.querySelector('[data-v2-gallery-previous]');
        this.next = this.querySelector('[data-v2-gallery-next]');
        this.dialog = this.querySelector('[data-v2-gallery-dialog]');
        this.dialogImage = this.querySelector('[data-v2-gallery-dialog-image]');
        this.activeIndex = 0;
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.softSnapDesktop = window.matchMedia('(min-width: 1024px)');
        this.hasNativeScrollEnd = 'onscrollend' in window;
        this.softSnapGesture = null;
        this.isSoftSnapping = false;
        this.softSnapLastStableY = window.scrollY;
        this.itemObserverReadyAt = Date.now() + 250;
        this.style.setProperty('--v2-gallery-count', String(Math.max(this.items.length, 1)));
        this.initializeDeferredMedia();

        this.handleTrackScroll = this.handleScroll.bind(this);
        this.handleWindowScroll = this.handleSoftSnapScroll.bind(this);
        this.handleWindowScrollEnd = this.handleSoftSnapScrollEnd.bind(this);
        this.handleWindowKeydown = this.handleSoftSnapKeydown.bind(this);
        this.track?.addEventListener('scroll', this.handleTrackScroll, { passive: true });
        window.addEventListener('scroll', this.handleWindowScroll, { passive: true });
        window.addEventListener('keydown', this.handleWindowKeydown);
        if (this.hasNativeScrollEnd) window.addEventListener('scrollend', this.handleWindowScrollEnd);
        this.previous?.addEventListener('click', () => this.show(this.activeIndex - 1));
        this.next?.addEventListener('click', () => this.show(this.activeIndex + 1));
        this.targets.forEach((target) => {
          target.addEventListener('click', () => this.show(Number(target.dataset.v2GalleryTarget)));
          target.addEventListener('keydown', (event) => this.handleTargetKeydown(event));
        });
        this.querySelectorAll('[data-v2-gallery-open]').forEach((button) => {
          button.addEventListener('click', () => this.open(button));
        });
        this.querySelector('[data-v2-gallery-close]')?.addEventListener('click', () => this.dialog?.close());
        this.dialog?.addEventListener('close', () => this.lastTrigger?.focus());
        this.dialog?.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.dialog.close();
        });
        this.observeDesktopItems();
        this.update(0);
      }

      disconnectedCallback() {
        this.track?.removeEventListener('scroll', this.handleTrackScroll);
        window.removeEventListener('scroll', this.handleWindowScroll);
        window.removeEventListener('keydown', this.handleWindowKeydown);
        if (this.hasNativeScrollEnd) window.removeEventListener('scrollend', this.handleWindowScrollEnd);
        window.clearTimeout(this.softSnapTimer);
        window.clearTimeout(this.softSnapReleaseTimer);
        this.itemObserver?.disconnect();
        this.deferredMediaObserver?.disconnect();
      }

      initializeDeferredMedia() {
        const placeholders = Array.from(this.querySelectorAll('[data-v2-deferred-gallery-media]'));
        const mount = (placeholder) => {
          const template = placeholder.querySelector('template');
          if (!template) return;
          placeholder.replaceChildren(template.content.cloneNode(true));
          placeholder.removeAttribute('data-v2-deferred-gallery-media');
        };

        if (!('IntersectionObserver' in window)) {
          placeholders.forEach(mount);
          return;
        }

        this.deferredMediaObserver = new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              mount(entry.target);
              observer.unobserve(entry.target);
            });
          },
          { rootMargin: '25% 50%' }
        );
        placeholders.forEach((placeholder) => this.deferredMediaObserver.observe(placeholder));
      }

      observeDesktopItems() {
        if (!('IntersectionObserver' in window) || !this.items.length) return;
        this.itemObserver = new IntersectionObserver(
          (entries) => {
            if (window.matchMedia('(max-width: 749px)').matches) return;
            if (Date.now() < this.itemObserverReadyAt) return;
            const visible = entries
              .filter((entry) => entry.isIntersecting)
              .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
            if (!visible) return;
            this.update(this.items.indexOf(visible.target));
          },
          { threshold: [0.2, 0.45, 0.7], rootMargin: '-10% 0px -35% 0px' }
        );
        this.items.forEach((item) => this.itemObserver.observe(item));
      }

      handleScroll() {
        if (!this.track || !this.items.length || !window.matchMedia('(max-width: 749px)').matches) return;
        const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
        this.update(index);
      }

      handleSoftSnapScroll() {
        if (!this.softSnapDesktop.matches || this.reducedMotion.matches || this.items.length < 2) return;
        if (this.isSoftSnapping) return;

        const scrollY = window.scrollY;
        if (!this.softSnapGesture) {
          this.softSnapGesture = { startY: this.softSnapLastStableY, lastY: scrollY };
        } else {
          this.softSnapGesture.lastY = scrollY;
        }

        window.clearTimeout(this.softSnapTimer);
        this.softSnapTimer = window.setTimeout(this.handleWindowScrollEnd, 140);
      }

      handleSoftSnapKeydown(event) {
        if (!['PageUp', 'PageDown', ' ', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
        this.softSnapKeyboardGuardUntil = Date.now() + 500;
      }

      handleSoftSnapScrollEnd() {
        window.clearTimeout(this.softSnapTimer);

        if (this.isSoftSnapping) {
          this.isSoftSnapping = false;
          this.softSnapGesture = null;
          this.softSnapLastStableY = window.scrollY;
          return;
        }

        const gesture = this.softSnapGesture;
        this.softSnapGesture = null;
        this.softSnapLastStableY = window.scrollY;
        if (!gesture || !this.softSnapDesktop.matches || this.reducedMotion.matches || this.items.length < 2) return;
        if (Date.now() < (this.softSnapKeyboardGuardUntil || 0)) return;

        const direction = Math.sign(gesture.lastY - gesture.startY);
        if (!direction) return;

        const header = document.querySelector('.v2-header');
        const headerHeight = header?.getBoundingClientRect().height || 0;
        const configuredHeaderHeight =
          Number.parseFloat(
            getComputedStyle(document.documentElement).getPropertyValue('--v2-header-desktop-height')
          ) || 57;
        const stickyShell = this.closest('.v2-product')?.querySelector('.v2-product-buybox__shell');
        const stickyTop = stickyShell ? Number.parseFloat(getComputedStyle(stickyShell).top) || 0 : 0;
        const offset = Math.max(stickyTop, (headerHeight || configuredHeaderHeight) + 12);
        const currentY = window.scrollY;
        const anchors = this.items.map((item) => currentY + item.getBoundingClientRect().top - offset);
        const firstAnchor = anchors[0];
        const lastAnchor = anchors[anchors.length - 1];
        const releaseDistance = Math.min(window.innerHeight * 0.3, 240);
        if (currentY < firstAnchor - 48 || currentY > lastAnchor + releaseDistance) return;

        const nearestIndex = anchors.reduce((bestIndex, anchor, index) => {
          return Math.abs(anchor - gesture.startY) < Math.abs(anchors[bestIndex] - gesture.startY) ? index : bestIndex;
        }, 0);
        let targetIndex = nearestIndex;

        if (direction > 0) {
          for (let index = nearestIndex + 1; index < anchors.length; index += 1) {
            const distance = anchors[index] - anchors[index - 1];
            const threshold = Math.min(220, Math.max(96, distance * 0.34));
            if (currentY < anchors[index - 1] + threshold) break;
            targetIndex = index;
          }
        } else {
          for (let index = nearestIndex - 1; index >= 0; index -= 1) {
            const distance = anchors[index + 1] - anchors[index];
            const threshold = Math.min(220, Math.max(96, distance * 0.34));
            if (currentY > anchors[index + 1] - threshold) break;
            targetIndex = index;
          }
        }

        if (targetIndex === nearestIndex) return;
        const targetY = anchors[targetIndex];
        this.update(targetIndex);
        if (Math.abs(targetY - currentY) < 2) return;

        this.isSoftSnapping = true;
        window.scrollTo({ top: targetY, behavior: 'smooth' });
        window.clearTimeout(this.softSnapReleaseTimer);
        this.softSnapReleaseTimer = window.setTimeout(() => {
          this.isSoftSnapping = false;
          this.softSnapGesture = null;
          this.softSnapLastStableY = window.scrollY;
        }, 800);
      }

      show(index) {
        if (!this.items.length) return;
        const normalized = Math.max(0, Math.min(index, this.items.length - 1));
        const behavior = this.reducedMotion.matches ? 'auto' : 'smooth';
        if (window.matchMedia('(max-width: 749px)').matches && this.track) {
          this.track.scrollTo({ left: normalized * this.track.clientWidth, behavior });
        } else {
          const headerOffset = Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--v2-header-desktop-height')) || 57;
          const top = window.scrollY + this.items[normalized].getBoundingClientRect().top - headerOffset - 12;
          window.scrollTo({ top, behavior });
        }
        this.update(normalized);
      }

      handleTargetKeydown(event) {
        const currentIndex = this.targets.indexOf(event.currentTarget);
        let nextIndex = null;
        if (['ArrowDown', 'ArrowRight'].includes(event.key)) nextIndex = Math.min(currentIndex + 1, this.targets.length - 1);
        if (['ArrowUp', 'ArrowLeft'].includes(event.key)) nextIndex = Math.max(currentIndex - 1, 0);
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = this.targets.length - 1;
        if (nextIndex === null) return;
        event.preventDefault();
        this.targets[nextIndex]?.focus();
        this.show(nextIndex);
      }

      update(index) {
        this.activeIndex = Math.max(0, Math.min(index, this.items.length - 1));
        this.items.forEach((item, itemIndex) => item.classList.toggle('is-active', itemIndex === this.activeIndex));
        this.targets.forEach((target, targetIndex) => {
          if (targetIndex === this.activeIndex) target.setAttribute('aria-current', 'true');
          else target.removeAttribute('aria-current');
        });
        if (this.current) this.current.textContent = String(this.activeIndex + 1).padStart(2, '0');
        if (this.status) {
          this.status.textContent = (this.status.dataset.labelTemplate || '').replace('__CURRENT__', String(this.activeIndex + 1));
        }
        if (this.previous) this.previous.disabled = this.activeIndex === 0;
        if (this.next) this.next.disabled = this.activeIndex === this.items.length - 1;
        if (this.progress) {
          this.progress.style.transform = `translateX(${this.activeIndex * 100}%)`;
        }
      }

      open(button) {
        if (!this.dialog || !this.dialogImage) return;
        this.lastTrigger = button;
        this.dialogImage.src = button.dataset.zoomSrc || '';
        this.dialogImage.alt = button.dataset.zoomAlt || '';
        this.dialog.showModal();
      }
    }
  );
}

function valesstraV2ProductUrl(url) {
  const target = new URL(url, window.location.origin);
  const current = new URL(window.location.href);
  if (current.pathname.endsWith(target.pathname)) target.pathname = current.pathname;
  for (const key of new Set(current.searchParams.keys())) {
    if (['variant', 'option_values', 'section_id'].includes(key) || target.searchParams.has(key)) continue;
    current.searchParams.getAll(key).forEach((value) => target.searchParams.append(key, value));
  }
  return target;
}

function isolateValesstraV2ProductInfo(productRoot) {
  if (productRoot.dataset.v2ProductInfoIsolated === 'true') return;
  productRoot.dataset.v2ProductInfoIsolated = 'true';

  productRoot.buildRequestUrlWithParams = function buildRequestUrlWithParams(
    url,
    optionValues,
    shouldFetchFullPage = false
  ) {
    const requestUrl = valesstraV2ProductUrl(url);
    requestUrl.searchParams.delete('variant');
    if (!shouldFetchFullPage) requestUrl.searchParams.set('section_id', this.sectionId);
    if (optionValues.length) requestUrl.searchParams.set('option_values', optionValues.join(','));
    return `${requestUrl.pathname}${requestUrl.search}`;
  };

  productRoot.updateURL = function updateURL(url, variantId) {
    const nextUrl = valesstraV2ProductUrl(url);
    nextUrl.searchParams.delete('option_values');
    nextUrl.searchParams.delete('section_id');
    if (variantId) nextUrl.searchParams.set('variant', variantId);
    else nextUrl.searchParams.delete('variant');
    const relativeUrl = `${nextUrl.pathname}${nextUrl.search}`;
    this.querySelector('share-button')?.updateUrl(`${window.shopUrl}${relativeUrl}`);
    if (this.dataset.updateUrl === 'false') return;
    window.history.replaceState({}, '', relativeUrl);
  };
}

function initializeValesstraV2StickyPurchase(productRoot) {
  if (productRoot.valesstraV2UpdateStickyPurchase) {
    productRoot.valesstraV2UpdateStickyPurchase();
    return;
  }

  const update = () => {
    const shell = productRoot.querySelector('.v2-product-buybox__shell');
    if (!shell) return;
    if (!shell.isConnected) return;
    const desktopLayout = window.matchMedia('(min-width: 750px)').matches;
    const stickyTop = Number.parseFloat(getComputedStyle(shell).top) || 0;
    const availableHeight = window.innerHeight - stickyTop - 16;
    const panelHeight = shell.getBoundingClientRect().height;
    const panelFits = panelHeight <= availableHeight + 0.5;

    shell.classList.toggle('is-sticky-safe', desktopLayout && panelFits);
    shell.classList.toggle('is-sticky-overflow', desktopLayout && !panelFits);

    if (desktopLayout && !panelFits) {
      const overflowTop = Math.min(stickyTop, window.innerHeight - panelHeight - 16);
      shell.style.setProperty('--v2-product-sticky-overflow-top', `${Math.floor(overflowTop)}px`);
    } else {
      shell.style.removeProperty('--v2-product-sticky-overflow-top');
    }
  };

  productRoot.valesstraV2UpdateStickyPurchase = update;
  const observer = new ResizeObserver(update);
  observer.observe(productRoot.querySelector('.v2-product-buybox__shell'));
  window.addEventListener('resize', update, { passive: true });
  update();
}

function initializeValesstraV2Product(root = document) {
  const productRoots = root.matches?.('.v2-product') ? [root] : Array.from(root.querySelectorAll('.v2-product'));
  productRoots.forEach((productRoot) => {
    isolateValesstraV2ProductInfo(productRoot);
    initializeValesstraV2StickyPurchase(productRoot);
    const relatedProducts = productRoot.querySelector('.v2-product-related');
    if (!relatedProducts) return;

    const loadRelatedProducts = () => {
      if (relatedProducts.dataset.v2Requested === 'true' || typeof relatedProducts.loadRecommendations !== 'function') {
        return;
      }
      relatedProducts.dataset.v2Requested = 'true';
      relatedProducts.observer?.unobserve(relatedProducts);
      relatedProducts.loadRecommendations(relatedProducts.dataset.productId);
    };

    if (customElements.get('product-recommendations')) loadRelatedProducts();
    else customElements.whenDefined('product-recommendations').then(loadRelatedProducts);
  });

  root.querySelectorAll('.v2-product-details__item').forEach((details) => {
    if (details.dataset.v2Ready === 'true') return;
    details.dataset.v2Ready = 'true';
    const summary = details.querySelector('summary');
    details.addEventListener('toggle', () => summary?.setAttribute('aria-expanded', String(details.open)));
    details.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !details.open) return;
      details.open = false;
      summary?.focus();
    });
  });

  root.querySelectorAll('.v2-product-buybox__review-summary').forEach((link) => {
    if (link.dataset.v2Ready === 'true') return;
    link.dataset.v2Ready = 'true';
    link.addEventListener('click', (event) => {
      const target = document.querySelector(link.getAttribute('href'));
      if (!target) return;
      event.preventDefault();
      target.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      target.focus({ preventScroll: true });
      window.history.replaceState({}, '', `${window.location.pathname}${window.location.search}#V2Reviews`);
    });
  });
}

function syncValesstraV2Variant(sectionId, html, variant) {
  const root = document.querySelector(`.v2-product[data-section="${sectionId}"]`);
  if (!root) return;

  const incomingMaterial = html.querySelector('[data-v2-material]');
  root.querySelectorAll('[data-v2-material]').forEach((material) => {
    material.innerHTML = incomingMaterial?.innerHTML || '';
    material.toggleAttribute('hidden', !incomingMaterial?.textContent.trim());
  });

  const currentProtection = root.querySelector('[data-v2-protection-selector]');
  const incomingProtection = html.querySelector('[data-v2-protection-selector]');
  if (currentProtection && incomingProtection) currentProtection.replaceWith(incomingProtection.cloneNode(true));
  else if (currentProtection && !incomingProtection) currentProtection.remove();

  const button = root.querySelector('.v2-product-buybox__submit');
  if (button && variant?.available && !button.disabled) {
    const label = root.dataset.v2AddLabel;
    const text = button.querySelector('span');
    if (text && label) text.textContent = label;
  }

  initializeValesstraV2Product(root);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeValesstraV2Product(), { once: true });
} else {
  initializeValesstraV2Product();
}

document.addEventListener('shopify:section:load', (event) => initializeValesstraV2Product(event.target));

if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
  subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
    syncValesstraV2Variant(data.sectionId, data.html, data.variant);
  });
}
